import { FC, ReactNode, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StyledAccordion, StyledAccordionSummary, StyledAccordionDetails } from './RateCardAccordionStyles';

export interface GenericAccordionProps {
  /**
   * Icon to display in the accordion header
   */
  icon: ReactNode;

  /**
   * Title text to display in the accordion header
   */
  title: string;

  /**
   * Count of service providers to display in the header
   */
  providerCount: number;

  /**
   * Optional right side content for the accordion header
   */
  headerRightContent?: ReactNode;

  /**
   * Optional id for targeting
   */
  id?: string;

  /**
   * Content to display when the accordion is expanded
   */
  children: ReactNode;

  /**
   * Optional initial expanded state
   */
  defaultExpanded?: boolean;

  /**
   * Optional callback when accordion expansion state changes
   */
  onAccordionChange?: (isExpanded: boolean) => void;
}

export const GenericAccordion: FC<GenericAccordionProps> = ({
  id,
  icon,
  title,
  providerCount,
  headerRightContent,
  children,
  defaultExpanded = false,
  onAccordionChange,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

  const handleChange = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onAccordionChange?.(newExpandedState);
  };

  return (
    <StyledAccordion id={id} expanded={isExpanded} onChange={handleChange}>
      <StyledAccordionSummary className='border-none border-0' expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.8rem', color: '#ffffff' }} />}>
        <div className='flex items-center pr-12 w-full'>
          <div className='flex-1'>
            <div className='flex-1 flex items-center gap-12'>
              <div className='flex items-center gap-8'>
                <div className='w-20 h-20 xl:w-24 xl:h-24 flex items-center justify-center'>{icon}</div>
                <strong className='text-14 xl:text-18 font-bold text-blue_grey-30'>{title}</strong>
              </div>
              <div className='text-10 xl:text-14 font-medium text-grey-40'>{providerCount} SERVICE PROVIDERS</div>
            </div>
          </div>
          {headerRightContent}
        </div>
      </StyledAccordionSummary>

      <StyledAccordionDetails>
        <div>{children}</div>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};
